<template>
  <v-card class="mb-4" :elevation="$vuetify.breakpoint.xsOnly ? 0 : ''">
    <v-card-title class="subtitle-1 text-uppercase font-weight-medium">
      <v-icon left>{{ typeIcon }}</v-icon>
      {{ $t('inventory.record_transaction') }}:&nbsp;<strong>{{ warehouseName }}</strong>
      <span v-if="rows.length > 0" class="caption text--secondary text-none ml-3" :title="$t('inventory.warehouse_locked_hint')">
        <v-icon x-small class="no-gradient">mdi-lock-outline</v-icon>
        {{ $t('inventory.warehouse_locked_short') }}
      </span>
    </v-card-title>
    <v-card-text>
      <v-btn-toggle
        v-if="$vuetify.breakpoint.smAndUp"
        :key="'toggle' + typeKey"
        :value="type"
        mandatory
        dense
        color="primary"
        class="mb-4"
        role="group"
        :aria-label="$t('inventory.transaction_type')"
        @change="onTypeChange"
      >
        <v-btn v-for="option in typeOptions" :key="option.value" :value="option.value" :aria-pressed="String(type === option.value)">
          <v-icon left small>{{ option.icon }}</v-icon>
          {{ option.text }}
        </v-btn>
      </v-btn-toggle>
      <v-select
        v-else
        :key="'select' + typeKey"
        :value="type"
        :items="typeOptions"
        item-text="text"
        item-value="value"
        :label="$t('inventory.transaction_type')"
        outlined
        dense
        hide-details
        class="mb-4"
        @change="onTypeChange"
      />

      <v-alert v-if="type === 'transfer' && targetWarehouses.length === 0" type="info" dense>
        {{ $t('inventory.no_other_warehouse') }}
      </v-alert>
      <template v-else>
        <v-form ref="form" @submit.prevent>
          <v-row>
            <v-col cols="12" sm="6">
              <v-text-field
                v-model="description"
                :label="$t('inventory.description') + ' *'"
                :rules="[v => !!v || $t('validation.required')]"
                hide-details="auto"
                outlined
                dense
              />
            </v-col>
            <v-col cols="6" sm="3">
              <v-menu v-model="dateMenu" :close-on-content-click="false" offset-y max-width="290px">
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    :value="formatIsoDateFi(transactionDate)"
                    :label="$t('inventory.transaction_date')"
                    readonly outlined dense hide-details
                    prepend-inner-icon="mdi-calendar"
                    v-bind="attrs" v-on="on"
                  />
                </template>
                <v-date-picker v-model="transactionDate" no-title locale="fi" :first-day-of-week="1" @input="dateMenu = false" />
              </v-menu>
            </v-col>
            <v-col v-if="type === 'transfer'" cols="6" sm="3">
              <v-select
                v-model="targetWarehouseId"
                :items="targetWarehouses"
                item-text="name"
                item-value="id"
                :label="$t('inventory.target_warehouse') + ' *'"
                :rules="[v => !!v || $t('validation.required')]"
                prepend-inner-icon="mdi-warehouse"
                hide-details="auto"
                outlined
                dense
              />
            </v-col>
          </v-row>
        </v-form>

        <v-row class="mt-2">
          <v-col cols="12" md="9">
            <item-combobox
              :value="item"
              :items="availableItems"
              :label="$t('inventory.select_item')"
              hide-details
              @change="onItemSelected"
            />
          </v-col>
          <v-col v-if="hasWarehouseUnit" cols="auto">
            <v-btn-toggle
              v-model="unitMode"
              mandatory
              dense
              role="group"
              :aria-label="$t('inventory.input_unit')"
              :title="$t('inventory.input_unit')"
              @change="onUnitModeChange"
            >
              <v-btn value="usage" small :aria-pressed="String(unitMode === 'usage')">{{ unitLabel(item) }}</v-btn>
              <v-btn value="warehouse" small :aria-pressed="String(unitMode === 'warehouse')">{{ item.warehouseUnit }}</v-btn>
            </v-btn-toggle>
          </v-col>
        </v-row>

        <v-row align="center">
          <v-col v-if="type === 'adjust'" cols="12" md="2">
            <v-text-field
              v-model.number="newQuantity"
              :label="$t('inventory.new_quantity')"
              type="number"
              min="0"
              step="any"
              :suffix="item ? unitLabel(item) : ''"
              :disabled="!item"
              outlined
              dense
              hide-details
              @keydown.enter.prevent="addRow"
            />
          </v-col>
          <v-col v-else-if="useWarehouseUnit" cols="12" md="2">
            <numeric-input
              v-model="amount"
              :label="$t('inventory.amount_in_unit', { unit: item.warehouseUnit })"
              :options="numberOptions"
              outlined
              dense
              hide-details
              @keydown.enter.native.prevent="addRow"
            />
          </v-col>
          <template v-else>
            <v-col cols="6" md="2">
              <v-text-field
                v-model.number="pieces"
                :label="$t('inventory.pieces')"
                type="number"
                min="1"
                :disabled="!item"
                outlined
                dense
                hide-details
                @keydown.enter.prevent="addRow"
              />
            </v-col>
            <v-col v-if="isMeterItem" cols="6" md="2">
              <v-text-field
                v-model.number="unitQuantity"
                :label="$t('inventory.meters_per_piece')"
                type="number"
                step="0.1"
                min="0.1"
                outlined
                dense
                hide-details
                @keydown.enter.prevent="addRow"
              />
            </v-col>
          </template>
          <v-col v-if="showPrice" cols="12" md="3">
            <numeric-input
              :key="priceKey"
              v-model="price"
              :label="item ? $t('inventory.unit_cost_in_unit', { unit: enteredUnit }) : $t('inventory.unit_cost')"
              :options="numberOptions"
              :disabled="!item || (type === 'adjust' && !adjustIncreases)"
              suffix="€"
              outlined
              dense
              hide-details
              @keydown.enter.native.prevent="addRow"
            />
          </v-col>
          <v-col v-if="loadingBalance || item" cols="12" md>
            <div v-if="loadingBalance" class="caption text--secondary">{{ $t('inventory.loading_balance') }}</div>
            <template v-else>
              <div class="caption text--secondary">
                {{ $t('inventory.current_balance') }}: {{ formatQuantity(currentBalance) }} {{ unitLabel(item) }}
              </div>
              <div v-if="type === 'adjust' && adjustDelta !== null" class="caption">
                {{ $t('inventory.change') }}: {{ signed(adjustDelta) }} {{ unitLabel(item) }}
              </div>
              <div v-else-if="type !== 'adjust' && enteredQuantity" class="caption">
                {{ $t('inventory.total_quantity') }}: {{ formatQuantity(enteredQuantity) }} {{ enteredUnit }}
              </div>
              <div v-if="enteredValue !== null" class="caption">{{ $t('inventory.row_value') }}: {{ formatEuro(enteredValue) }}</div>
              <div v-if="rowHint" class="caption error--text">{{ rowHint }}</div>
              <div v-if="balanceWarning" class="caption warning--text">{{ balanceWarning }}</div>
            </template>
          </v-col>
          <v-col cols="12" md="2" class="ml-auto">
            <v-btn color="primary" outlined :disabled="!canAddRow" block @click="addRow">
              <v-icon left>mdi-plus</v-icon>
              {{ $t('inventory.add_row') }}
            </v-btn>
          </v-col>
        </v-row>

        <v-simple-table v-if="rows.length > 0" class="mt-4">
          <template v-slot:default>
            <thead>
              <tr>
                <th>{{ $t('inventory.item_name') }}</th>
                <th>{{ $t('inventory.current_balance') }}</th>
                <th>{{ $t('inventory.pieces') }}</th>
                <th>{{ type === 'adjust' ? $t('inventory.new_quantity') : $t('inventory.amount') }}</th>
                <th v-if="showPrice">{{ $t('inventory.unit_cost') }}</th>
                <th v-if="showPrice">{{ $t('inventory.row_value') }}</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(row, index) in rows" :key="index">
                <td>{{ row.item.name }}</td>
                <td>{{ formatQuantity(row.currentBalance) }} {{ unitLabel(row.item) }}</td>
                <td>{{ piecesLabel(row) }}</td>
                <td>
                  {{ formatQuantity(row.enteredQuantity) }} {{ row.enteredUnit }}
                  <span v-if="type === 'adjust'" class="text--secondary">({{ signed(row.delta) }} {{ row.enteredUnit }})</span>
                </td>
                <td v-if="showPrice">
                  <template v-if="row.enteredPrice !== null">{{ formatEuro(row.enteredPrice) }} / {{ row.enteredUnit }}</template>
                </td>
                <td v-if="showPrice">{{ formatEuro(row.value) }}</td>
                <td>
                  <v-btn icon small :aria-label="$t('inventory.remove_row')" @click="rows.splice(index, 1)">
                    <v-icon small color="error" class="no-gradient">mdi-delete</v-icon>
                  </v-btn>
                </td>
              </tr>
            </tbody>
          </template>
        </v-simple-table>
        <div v-else class="caption text--secondary mt-4">{{ $t('inventory.no_items_added') }}</div>

        <v-alert v-if="saveError" type="error" dense class="mt-4 mb-0">{{ saveError }}</v-alert>
      </template>
    </v-card-text>
    <v-card-actions v-if="type !== 'transfer' || targetWarehouses.length > 0" :class="$vuetify.breakpoint.xsOnly && 'flex-column-reverse'">
      <v-btn v-if="rows.length > 0" text :block="$vuetify.breakpoint.xsOnly" @click="clearRows">
        {{ $t('inventory.clear_rows') }}
      </v-btn>
      <v-spacer />
      <v-btn
        color="primary"
        :block="$vuetify.breakpoint.xsOnly"
        :class="$vuetify.breakpoint.xsOnly && 'mb-2'"
        :disabled="rows.length === 0"
        :loading="saving"
        @click="save"
      >
        <v-icon left>{{ typeIcon }}</v-icon>
        {{ saveLabel }}
      </v-btn>
    </v-card-actions>
    <confirm-dialog ref="confirm" />
  </v-card>
</template>

<script>
import inventoryApi from '@/api/inventory';
import ConfirmDialog from '@/components/ConfirmDialog';
import ItemCombobox from '@/components/ItemCombobox';
import NumericInput from '@/components/NumericInput';
import { PIECES_UNIT, formatEuro, formatIsoDateFi, formatQuantity, todayIso, unitLabel } from '@/helpers/inventoryFormat';

const TYPE_ICONS = { add: 'mdi-plus', remove: 'mdi-minus', adjust: 'mdi-pencil', transfer: 'mdi-swap-horizontal' };

const round4 = v => Math.round(v * 10000) / 10000;

export default {
  name: 'InventoryTransactionForm',
  components: { ConfirmDialog, ItemCombobox, NumericInput },
  props: {
    // Tapahtumatyyppi: add | remove | adjust | transfer
    value: {
      type: String,
      default: 'add',
    },
    warehouseId: {
      type: Number,
      default: null,
    },
    warehouses: {
      type: Array,
      default: () => [],
    },
    items: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      type: TYPE_ICONS[this.value] ? this.value : 'add',
      // Tyyppivalitsin luodaan uudelleen, kun vaihto perutaan vahvistusdialogissa.
      typeKey: 0,
      targetWarehouseId: null,
      description: '',
      transactionDate: todayIso(),
      dateMenu: false,
      item: null,
      unitMode: 'usage',
      pieces: null,
      unitQuantity: null,
      amount: null,
      newQuantity: null,
      price: null,
      // NumericInput ei seuraa ulkoisia muutoksia, joten esitäytetty hinta näkyy vasta kun kenttä luodaan uudelleen
      priceKey: 0,
      currentBalance: null,
      loadingBalance: false,
      rows: [],
      saving: false,
      saveError: null,
      numberOptions: {
        decimalCharacter: ',',
        digitGroupSeparator: ' ',
        decimalPlaces: 4,
        allowDecimalPadding: false,
        emptyInputBehavior: 'null',
      },
    };
  },
  computed: {
    typeOptions() {
      return [
        { value: 'add', text: this.$t('inventory.add_to_warehouse'), icon: TYPE_ICONS.add },
        { value: 'remove', text: this.$t('inventory.remove_from_warehouse'), icon: TYPE_ICONS.remove },
        { value: 'adjust', text: this.$t('inventory.adjust_balance'), icon: TYPE_ICONS.adjust },
        { value: 'transfer', text: this.$t('inventory.transfer_between_warehouses'), icon: TYPE_ICONS.transfer },
      ];
    },
    typeIcon() {
      return TYPE_ICONS[this.type];
    },
    warehouseName() {
      const warehouse = this.warehouses.find(w => w.id === this.warehouseId);
      return warehouse ? warehouse.name : '';
    },
    targetWarehouses() {
      return this.warehouses.filter(w => w.id !== this.warehouseId);
    },
    targetWarehouseName() {
      const warehouse = this.warehouses.find(w => w.id === this.targetWarehouseId);
      return warehouse ? warehouse.name : '';
    },
    saveLabel() {
      const warehouse = this.warehouseName;
      switch (this.type) {
        case 'remove':
          return this.$t('inventory.remove_from_named_warehouse', { warehouse });
        case 'adjust':
          return this.$t('inventory.adjust_named_warehouse', { warehouse });
        case 'transfer':
          return this.targetWarehouseName
            ? this.$t('inventory.transfer_named', { from: warehouse, to: this.targetWarehouseName })
            : this.$t('inventory.transfer');
        default:
          return this.$t('inventory.add_to_named_warehouse', { warehouse });
      }
    },
    availableItems() {
      // Saldokorjauksessa sama nimike kahdesti laskisi deltan vanhentuneesta saldosta.
      if (this.type !== 'adjust') return this.items;
      const addedIds = this.rows.map(r => r.itemId);
      return this.items.filter(i => !addedIds.includes(i.id));
    },
    isMeterItem() {
      return !!this.item && this.item.unit === 'm';
    },
    hasWarehouseUnit() {
      return this.type !== 'adjust' && !!(this.item && this.item.warehouseUnit && this.item.conversionFactor);
    },
    useWarehouseUnit() {
      return this.hasWarehouseUnit && this.unitMode === 'warehouse';
    },
    showPrice() {
      return this.type === 'add' || this.type === 'adjust';
    },
    enteredUnit() {
      if (!this.item) return '';
      return this.useWarehouseUnit ? this.item.warehouseUnit : unitLabel(this.item);
    },
    // Määrä käyttöyksikössä (adjust: delta nykysaldoon)
    enteredQuantity() {
      if (this.type === 'adjust') return this.adjustDelta;
      if (this.useWarehouseUnit) return this.amount;
      if (!this.pieces) return null;
      if (this.isMeterItem) return this.unitQuantity ? this.pieces * this.unitQuantity : null;
      return this.pieces;
    },
    adjustDelta() {
      if (this.newQuantity === null || this.newQuantity === '') return null;
      return Number(this.newQuantity) - Number(this.currentBalance || 0);
    },
    adjustIncreases() {
      return this.type === 'adjust' && this.adjustDelta !== null && this.adjustDelta > 0;
    },
    hasPrice() {
      return this.price !== null && this.price !== undefined && this.price >= 0;
    },
    exceedsBalance() {
      if (this.type !== 'remove' && this.type !== 'transfer') return false;
      return this.enteredQuantity !== null && this.enteredQuantity > Number(this.currentBalance || 0);
    },
    enteredValue() {
      if (!this.showPrice || !this.hasPrice) return null;
      const quantity = this.type === 'adjust' ? this.adjustDelta : this.enteredQuantity;
      if (!quantity || quantity < 0) return null;
      return quantity * this.price;
    },
    rowHint() {
      if (!this.item) return null;
      if (this.type === 'adjust') {
        if (this.adjustDelta === 0) return this.$t('inventory.same_as_current_balance');
        if (this.adjustIncreases && !this.hasPrice) return this.$t('inventory.unit_cost_required_on_increase');
        return null;
      }
      if (this.isMeterItem && !this.useWarehouseUnit && this.pieces && !(this.unitQuantity > 0)) {
        return this.$t('inventory.meters_per_piece_required');
      }
      if (this.exceedsBalance && this.type === 'transfer') return this.$t('inventory.exceeds_balance');
      if (this.type === 'add' && this.enteredQuantity > 0 && !this.hasPrice) return this.$t('inventory.unit_cost_required');
      return null;
    },
    // Otto saa viedä saldon miinukselle, joten ylityksestä vain varoitetaan.
    balanceWarning() {
      return this.type === 'remove' && this.exceedsBalance ? this.$t('inventory.balance_goes_negative') : null;
    },
    canAddRow() {
      if (!this.item || this.loadingBalance) return false;
      if (this.type === 'adjust') {
        if (this.adjustDelta === null || Number(this.newQuantity) < 0 || this.adjustDelta === 0) return false;
        return !this.adjustIncreases || this.hasPrice;
      }
      if (!(this.enteredQuantity > 0)) return false;
      if (this.type === 'add') return this.hasPrice;
      return this.type === 'remove' || !this.exceedsBalance;
    },
  },
  watch: {
    value(newType) {
      if (newType !== this.type && this.rows.length === 0 && TYPE_ICONS[newType]) {
        this.type = newType;
        this.clearRowInput();
      }
    },
    'rows.length'(length) {
      this.$emit('pending', length > 0);
    },
    warehouseId() {
      if (this.targetWarehouseId === this.warehouseId) this.targetWarehouseId = null;
      // Nykysaldo on varastokohtainen.
      this.clearRowInput();
    },
  },
  beforeDestroy() {
    this.$emit('pending', false);
  },
  methods: {
    formatQuantity,
    formatEuro,
    formatIsoDateFi,
    unitLabel,

    async onTypeChange(newType) {
      if (!newType || newType === this.type) return;
      if (this.rows.length > 0) {
        const confirmed = await this.$refs.confirm.open(
          this.$t('inventory.change_type_title'),
          this.$tc('inventory.change_type_message', this.rows.length, { count: this.rows.length }),
          { color: 'primary', agree: this.$t('inventory.change_type_confirm') }
        );
        if (!confirmed) {
          this.typeKey++;
          return;
        }
        this.rows = [];
      }
      this.type = newType;
      this.saveError = null;
      this.clearRowInput();
      this.$emit('input', newType);
    },

    async onItemSelected(item) {
      this.item = item || null;
      this.unitMode = 'usage';
      this.pieces = null;
      this.unitQuantity = null;
      this.amount = null;
      this.newQuantity = null;
      this.currentBalance = null;
      this.prefillPrice();
      if (!this.item) return;
      this.loadingBalance = true;
      try {
        const balance = await inventoryApi.getWarehouseBalanceForItem(this.item.id, this.warehouseId);
        this.currentBalance = balance ? balance.quantity : 0;
      } catch (error) {
        this.currentBalance = 0;
      } finally {
        this.loadingBalance = false;
      }
    },

    // Nimikkeen hinta on varastoyksikköä kohti, kun nimikkeellä on varastoyksikkö ja kerroin.
    prefillPrice() {
      const item = this.item;
      if (!this.showPrice || !item || item.price === null || item.price === undefined) {
        this.price = null;
      } else if (item.warehouseUnit && item.conversionFactor && !this.useWarehouseUnit) {
        this.price = round4(item.price * item.conversionFactor);
      } else {
        this.price = item.price;
      }
      this.priceKey++;
    },

    // Yksikön vaihto muuntaa syötetyn määrän ja hinnan toiseen yksikköön, jolloin rivin arvo säilyy.
    onUnitModeChange() {
      const factor = this.item.conversionFactor;
      if (this.useWarehouseUnit) {
        const quantity = this.isMeterItem && this.unitQuantity ? this.pieces * this.unitQuantity : this.pieces;
        this.amount = quantity ? round4(quantity * factor) : null;
        if (this.price !== null) this.price = round4(this.price / factor);
      } else {
        const quantity = this.amount ? this.amount / factor : null;
        const perPiece = this.isMeterItem && this.unitQuantity ? this.unitQuantity : 1;
        this.pieces = quantity ? round4(quantity / perPiece) : null;
        if (this.price !== null) this.price = round4(this.price * factor);
      }
      this.priceKey++;
    },

    addRow() {
      if (!this.canAddRow) return;
      const item = this.item;
      const row = {
        itemId: item.id,
        item,
        currentBalance: this.currentBalance || 0,
        enteredUnit: this.enteredUnit,
        enteredPrice: this.showPrice ? this.price : null,
        value: this.enteredValue,
        pieces: null,
        unitQuantity: null,
        unitCost: null,
        delta: null,
      };
      if (this.type === 'adjust') {
        Object.assign(row, {
          quantity: this.adjustDelta,
          delta: this.adjustDelta,
          enteredQuantity: Number(this.newQuantity),
          unitCost: this.adjustIncreases ? this.price : null,
          enteredPrice: this.adjustIncreases ? this.price : null,
        });
      } else if (this.useWarehouseUnit) {
        const factor = item.conversionFactor;
        Object.assign(row, {
          quantity: this.amount / factor,
          enteredQuantity: this.amount,
          unitCost: this.type === 'add' ? this.price * factor : null,
        });
      } else {
        Object.assign(row, {
          quantity: this.enteredQuantity,
          enteredQuantity: this.enteredQuantity,
          pieces: this.pieces,
          unitQuantity: this.isMeterItem ? this.unitQuantity : null,
          unitCost: this.type === 'add' ? this.price : null,
        });
      }
      this.rows.push(row);
      this.saveError = null;
      this.clearRowInput();
    },

    clearRowInput() {
      this.item = null;
      this.unitMode = 'usage';
      this.pieces = null;
      this.unitQuantity = null;
      this.amount = null;
      this.newQuantity = null;
      this.price = null;
      this.priceKey++;
      this.currentBalance = null;
      this.loadingBalance = false;
    },

    clearRows() {
      this.rows = [];
      this.saveError = null;
    },

    piecesLabel(row) {
      if (!row.pieces) return '';
      const pieces = `${row.pieces} ${PIECES_UNIT}`;
      return row.unitQuantity ? `${pieces} × ${formatQuantity(row.unitQuantity)} ${unitLabel(row.item)}` : pieces;
    },

    signed(value) {
      return (value > 0 ? '+' : '') + formatQuantity(value);
    },

    rowSummary() {
      return this.rows
        .map(row => {
          const quantity = `${formatQuantity(row.enteredQuantity)} ${row.enteredUnit}`;
          return this.type === 'adjust'
            ? `${row.item.name} ${quantity} (${this.signed(row.delta)})`
            : `${row.item.name} ${quantity}`;
        })
        .join(', ');
    },

    submitRow(row) {
      const common = {
        itemId: row.itemId,
        warehouseId: this.warehouseId,
        quantity: row.quantity,
        pieces: row.pieces,
        unitQuantity: row.unitQuantity,
        description: this.description,
        transactionDate: this.transactionDate,
      };
      switch (this.type) {
        case 'remove':
          return inventoryApi.removeFromWarehouse(common);
        case 'adjust':
          return inventoryApi.adjustBalance({
            ...common,
            unitCost: row.delta > 0 ? row.unitCost : null,
            targetType: 'WAREHOUSE',
          });
        case 'transfer':
          return inventoryApi.transferBetweenWarehouses({ ...common, targetWarehouseId: this.targetWarehouseId });
        default:
          return inventoryApi.addToWarehouse({ ...common, unitCost: row.unitCost });
      }
    },

    successMessage() {
      const warehouse = this.warehouseName;
      switch (this.type) {
        case 'remove':
          return this.$t('inventory.success.removed', { warehouse });
        case 'adjust':
          return this.$t('inventory.success.adjusted', { warehouse });
        case 'transfer':
          return this.$t('inventory.success.transferred', { from: warehouse, to: this.targetWarehouseName });
        default:
          return this.$t('inventory.success.added', { warehouse });
      }
    },

    async save() {
      if (this.rows.length === 0 || !this.$refs.form.validate()) return;
      // Otto, korjaus ja siirto muuttavat saldoja suoraan, joten rivit vahvistetaan ennen kirjausta.
      if (this.type !== 'add') {
        const confirmed = await this.$refs.confirm.open(
          this.saveLabel,
          this.$tc('inventory.confirm_rows', this.rows.length, { count: this.rows.length, summary: this.rowSummary() }),
          { color: 'primary', agree: this.saveLabel, width: 520, info: this.$t('inventory.confirm_rows_info') }
        );
        if (!confirmed) return;
      }
      this.saving = true;
      this.saveError = null;
      let saved = 0;
      try {
        // Onnistunut rivi poistetaan heti, jottei uusi yritys virheen jälkeen kirjaa sitä toiseen kertaan.
        while (this.rows.length > 0) {
          await this.submitRow(this.rows[0]);
          this.rows.shift();
          saved++;
        }
        this.$store.dispatch('showNotification', { color: 'success', message: this.successMessage(), timeOut: 2000 });
        this.description = '';
        this.$refs.form.resetValidation();
        this.$emit('saved');
      } catch (error) {
        console.error('Error saving inventory transaction:', error);
        this.saveError = this.$t('inventory.errors.row_failed', { item: this.rows[0].item.name });
        if (saved > 0) this.$emit('saved');
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>
